import { useCurrentUser } from '@hero/hooks-shared';
import type { UseOrderAgainConfig } from '@hero/hooks-shared';
import { AccountDrawer } from '../../Account/Drawer/AccountDrawer';
import { AccountTransactionDrawer } from '../../AccountTransaction/Drawer/AccountTransactionDrawer';
import { ClientDrawer } from '../../Client/Drawer/ClientDrawer';
import { ContactDrawer } from '../../Contact/Drawer/ContactDrawer';
import { DriverDrawer } from '../../Driver/Drawer/DriverDrawer';
import { InvoiceDrawer } from '../../Invoice/Drawer/InvoiceDrawer';
import { OrderDrawer } from '../../Order/Drawer/OrderDrawer';
import { SavedLocationDrawer } from '../../SavedLocation/Drawer/SavedLocationDrawer';
import { VehicleDrawer } from '../../Vehicle/Drawer/VehicleDrawer';

export interface AgentEntityDrawersProps {
  displayForUser?: 'customer' | 'carrier';
  /** Passed through to {@link OrderDrawer} for reorder flows inside the drawer. */
  orderAgainConfig?: UseOrderAgainConfig;
}

/**
 * Dashboard-level entity drawers (order, contact, client, invoice, …).
 *
 * Mount **once** at the dashboard layout. Every table row, CRM card, and
 * AI chat deep-link opens these via the shared Zustand drawer stores.
 * Mounting a second `<OrderDrawer />` / `<ContactDrawer />` on a page
 * duplicates `StackedDrawer` sheets and causes close flicker or drawers
 * that never fully dismiss.
 */
export const AgentEntityDrawers = ({
  displayForUser = 'carrier',
  orderAgainConfig,
}: AgentEntityDrawersProps) => {
  const { orgId } = useCurrentUser();

  return (
    <>
      <ClientDrawer displayForUser={displayForUser} />
      <ContactDrawer syncUrl displayForUser={displayForUser} />
      <OrderDrawer
        syncUrl
        displayForUser={displayForUser}
        orderAgainConfig={orderAgainConfig}
      />
      <DriverDrawer syncUrl />
      <VehicleDrawer syncUrl />
      <SavedLocationDrawer displayForUser={displayForUser} />
      <InvoiceDrawer asRole="auto" displayForUser={displayForUser} viewerOrgId={orgId} />
      <AccountDrawer viewerOrgId={orgId} asRole="auto" />
      <AccountTransactionDrawer />
    </>
  );
};
